import { KeyRound } from 'lucide-react';
import type { NavItem, NavDrillGroup } from '../NavBar/nav-types';
import {
  CONFIGURE_GROUP_ORDER,
  CONFIGURE_HUB_ROOTS,
  CONFIGURE_SEARCH_LABELS,
  type ConfigureGroupLabel,
  orgRelativePath,
  resolveConfigureSection,
} from './configure-sections';

export interface ConfigureCommandGroup {
  /** Group heading rendered in the command palette. */
  label: string;
  items: NavItem[];
}

export interface ConfigureCommandTeam {
  id: string;
  name: string;
}

export interface BuildConfigureCommandGroupsOptions {
  /**
   * Teams the caller belongs to. The palette adds a Team secrets row per
   * team so Cmd+K can land on any of them, not only the first membership.
   */
  teams?: readonly ConfigureCommandTeam[];
}

/**
 * The unlabeled Configure group (Network Policies) has no heading in the
 * sidebar takeover. The palette has no unlabeled-group concept, so map
 * the empty label to a real heading.
 */
const PALETTE_GROUP_HEADING: Readonly<Record<ConfigureGroupLabel, string>> = {
  Build: 'Build',
  Connect: 'Connect',
  People: 'People',
  'Usage and Costs': 'Usage and Costs',
  '': 'Network',
  Organization: 'Organization',
  Company: 'Company',
};

/**
 * Build the Configure command-palette groups from the same drill groups and
 * mapping table the sidebar uses. Unlike {@link buildConfigureNav}, this:
 *
 * - walks sub-drills (`parentBasePath`) so per-provider integrations appear
 * - passes `includeHidden: true` so folded Credentials / Organization /
 *   Agents pages stay searchable with disambiguating labels
 * - maps the unlabeled Network Policies group to a "Network" heading
 * - rewrites hub entry points (Credentials) to the short hub root, and when
 *   a search label differs (Your model providers) emits a companion entry
 * - injects a Team secrets row for each of the caller's teams
 */
export function buildConfigureCommandGroups(
  slug: string,
  configureDrillGroups: readonly NavDrillGroup[],
  options: BuildConfigureCommandGroupsOptions = {},
): ConfigureCommandGroup[] {
  if (!slug) return [];

  // Include 'Connect' in the palette buckets so Connect pages remain
  // searchable via the command palette, even though they moved to the
  // dedicated Connect takeover surface.
  const buckets = new Map<ConfigureGroupLabel, NavItem[]>(
    [...CONFIGURE_GROUP_ORDER, 'Connect' as const].map((g) => [g, []]),
  );
  const seen = new Set<string>();

  for (const group of configureDrillGroups) {
    for (const item of group.items) {
      const relative = orgRelativePath(item.to, slug);
      const mapping = resolveConfigureSection(relative, group.label, {
        includeHidden: true,
      });
      if (!mapping) continue;

      const hubRoot = CONFIGURE_HUB_ROOTS[relative];
      const entryTo = hubRoot ? `/orgs/${slug}${hubRoot}` : item.to;
      if (seen.has(entryTo)) continue;
      seen.add(entryTo);

      const label = mapping.label ?? item.label;
      const entry: NavItem = {
        ...item,
        to: entryTo,
        label,
      };
      const bucket = buckets.get(mapping.group);
      if (relative === '/configure/organization') bucket?.unshift(entry);
      else bucket?.push(entry);

      // Companion search row for hub entry points that also have a concrete
      // page label (Credentials hub + "Your model providers").
      const searchLabel = CONFIGURE_SEARCH_LABELS[relative];
      if (searchLabel && searchLabel !== label && !seen.has(item.to)) {
        seen.add(item.to);
        bucket?.push({
          ...item,
          label: searchLabel,
        });
      }
    }
  }

  const teams = options.teams ?? [];
  const disambiguateTeamSecrets = teams.length > 1;
  for (const team of teams) {
    const teamSecretsTo = `/orgs/${slug}/configure/connect/credentials/team/${team.id}/secrets`;
    if (seen.has(teamSecretsTo)) continue;
    seen.add(teamSecretsTo);
    buckets.get('Connect')?.push({
      to: teamSecretsTo,
      label: disambiguateTeamSecrets ? `Team secrets · ${team.name}` : 'Team secrets',
      icon: KeyRound,
    });
  }

  const allLabels: ConfigureGroupLabel[] = [...CONFIGURE_GROUP_ORDER, 'Connect'];
  return allLabels
    .filter((label) => (buckets.get(label) ?? []).length > 0)
    .map((label) => ({
      label: PALETTE_GROUP_HEADING[label],
      items: buckets.get(label) ?? [],
    }));
}

/** Flat set of every Configure command route — used to dedupe plugin commands. */
export function configureCommandRoutes(
  groups: readonly ConfigureCommandGroup[],
): ReadonlySet<string> {
  return new Set(groups.flatMap((g) => g.items.map((i) => i.to)));
}
